import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Building2, Plus, Pencil, Trash2, FileText, Landmark, Receipt } from 'lucide-react';
import {
  PageHead, Panel, Table, Row, Cell, Pill, Modal, ConfirmModal,
  FormField, FormSection, btn, field,
} from '../common/AdminUI';
import { listCreditors, createCreditor, updateCreditor, deleteCreditor, money } from './reconApi';
import PurViewerModal from './PurViewerModal';
import LedgerButton from './LedgerButton';

/**
 * Step 1 — the carriers and agents we are billed by.
 *
 * A creditor is what a PUR hangs off: the upload screen asks which creditor sent
 * the bill before it reads anything, because every one of them lays its invoice
 * out differently.
 */

/* ── what a valid number looks like ──────────────────────────────────────────
   These are worth checking in the browser because they are copied off a PDF by
   hand, and a wrong GSTIN is only ever found much later, by somebody else. */

const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const TAN_RE = /^[A-Z]{4}[0-9]{5}[A-Z]$/;
const GST_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]{3}$/;
// Listed/unlisted, industry, state, year of incorporation, ownership, number.
const CIN_RE = /^[LU][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/;

/** What stops a save: a number that is not the shape its issuer uses. */
const validate = (f) => {
  const e = {};
  if (!f.name.trim()) e.name = 'Required';
  if (f.pan && !PAN_RE.test(f.pan)) e.pan = 'Looks like AAICM9435C';
  if (f.tan && !TAN_RE.test(f.tan)) e.tan = 'Looks like DELM23075D';
  if (f.gstNo && !GST_RE.test(f.gstNo)) e.gstNo = 'A GSTIN is 15 characters';
  if (f.cin && !CIN_RE.test(f.cin)) e.cin = 'Looks like U51909DL2005PLC123456';
  return e;
};

/**
 * What is worth saying but not worth refusing over.
 *
 * A GSTIN carries the PAN at characters 3–12, so when both are filled and they
 * disagree, one of them is wrong — but both are well formed and only the person
 * holding the paperwork knows which. This used to block the save, and it read
 * as though a perfectly good GSTIN were being rejected.
 */
const crossCheck = (f) => {
  if (!f.gstNo || !f.pan) return {};
  if (!GST_RE.test(f.gstNo) || !PAN_RE.test(f.pan)) return {};
  const inGst = f.gstNo.slice(2, 12);
  if (inGst === f.pan) return {};
  return { pan: `This GSTIN says the PAN is ${inGst}. Check which of the two is right.` };
};

const BLANK = { name: '', address: '', tan: '', pan: '', gstNo: '', cin: '' };

const CreditorForm = ({ open, onClose, initial, onSaved }) => {
  const [form, setForm] = useState(BLANK);
  const [errors, setErrors] = useState({});
  const warnings = crossCheck(form);
  const [busy, setBusy] = useState(false);
  const editing = Boolean(initial?.id);

  useEffect(() => { if (open) { setForm(initial ? { ...BLANK, ...initial } : BLANK); setErrors({}); } }, [open, initial]);

  // TAN, PAN and GSTIN are always upper case; forcing it here saves the
  // validation from failing on something the accountant typed correctly.
  const set = (k, upper) => (e) => {
    const v = upper ? e.target.value.toUpperCase().replace(/\s/g, '') : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((x) => ({ ...x, [k]: undefined }));
  };

  const save = async () => {
    const e = validate(form);
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    try {
      const saved = editing ? await updateCreditor(initial.id, form) : await createCreditor(form);
      toast.success(editing ? 'Creditor updated' : 'Creditor created');
      onSaved(saved);
      onClose();
    } catch (err) {
      toast.error(err.message || 'Could not save the creditor');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open} onClose={busy ? () => {} : onClose}
      icon={Building2} tone="violet"
      title={editing ? 'Edit creditor' : 'Create creditor'}
      subtitle="The carrier or agent whose purchase bills we receive"
      width="max-w-2xl"
      footer={(
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2.5">
          <button type="button" onClick={onClose} disabled={busy} className={`${btn.quiet} disabled:opacity-50`}>Cancel</button>
          <button type="button" onClick={save} disabled={busy} className={`${btn.primary} disabled:opacity-60`}>
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Create creditor'}
          </button>
        </div>
      )}
    >
      <FormSection title="Who they are" icon={Building2} tone="violet">
        <FormField label="Creditor name" required error={errors.name} className="md:col-span-2">
          <input className={field} value={form.name} onChange={set('name')}
            placeholder="Malindo Airways SDN. BHD" />
        </FormField>
        <FormField label="Address" className="md:col-span-2">
          <textarea rows={2} className={`${field} h-auto py-2.5 resize-none`} value={form.address}
            onChange={set('address')} placeholder="Building, street, city, PIN" />
        </FormField>
      </FormSection>

      <FormSection title="Statutory numbers"
        description="All optional — a foreign carrier will not have most of them. Each is checked, so a mistyped number is caught here and not next quarter"
        icon={FileText} tone="blue">
        <FormField label="GST number" hint="15 characters" error={errors.gstNo}>
          <input className={`${field} font-mono`} value={form.gstNo} onChange={set('gstNo', true)}
            maxLength={15} placeholder="07AAICM9435C1ZF" />
        </FormField>
        <FormField label="PAN" error={errors.pan}>
          <input className={`${field} font-mono`} value={form.pan} onChange={set('pan', true)}
            maxLength={10} placeholder="AAICM9435C" />
          {!errors.pan && warnings.pan && (
            <p className="mt-1.5 text-[11.5px] font-semibold text-amber-600 dark:text-amber-400">
              {warnings.pan}
            </p>
          )}
        </FormField>
        <FormField label="TAN" error={errors.tan}>
          <input className={`${field} font-mono`} value={form.tan} onChange={set('tan', true)}
            maxLength={10} placeholder="DELM23075D" />
        </FormField>
        <FormField label="CIN" hint="21 characters" error={errors.cin}>
          <input className={`${field} font-mono`} value={form.cin} onChange={set('cin', true)}
            maxLength={21} placeholder="U51909DL2005PLC123456" />
        </FormField>
      </FormSection>
    </Modal>
  );
};

/** `embedded` drops the page header, for when this sits inside another
 *  page's tabs; `onCount` lets that page show the number on its tab. */
const CreditorMaster = ({ search = '', onSearch, embedded = false, onCount }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const list = await listCreditors();
      setRows(list);
      onCount?.(list.length);
    }
    catch { toast.error('Could not load creditors'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((c) => [c.name, c.gstNo, c.pan, c.tan, c.address]
      .some((v) => String(v || '').toLowerCase().includes(q)));
  }, [rows, search]);

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (c) => { setEditing(c); setFormOpen(true); };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await deleteCreditor(toDelete.id);
      setRows((r) => { const next = r.filter((x) => x.id !== toDelete.id); onCount?.(next.length); return next; });
      toast.success('Creditor removed');
      setToDelete(null);
    } catch { toast.error('Could not remove the creditor'); }
    finally { setDeleting(false); }
  };

  const newButton = (
    <button type="button" className={btn.primary} onClick={openNew}>
      <Plus size={16} /> Create Creditor
    </button>
  );

  return (
    <div>
      {embedded ? (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <p className="text-[12.5px] text-slate-500 dark:text-slate-400">
            The carriers and agents that bill us. A purchase bill is read against the creditor that sent it.
          </p>
          {newButton}
        </div>
      ) : (
        <PageHead
          eyebrow="Accounts · Masters"
          title="Creditors"
          badge={<Pill tone="violet">{rows.length} on file</Pill>}
          subtitle="The carriers and agents that bill us. A purchase bill is read against the creditor that sent it."
          actions={newButton}
        />
      )}

      <Panel>
        <Table
          head={[
            { label: 'Creditor' },
            { label: 'GSTIN' },
            { label: 'PAN' },
            { label: 'TAN' },
            { label: 'CIN' },
            { label: 'Balance', align: 'right' },
            { label: 'Actions' },
          ]}
          empty={loading ? 'Loading creditors…' : search ? 'No creditor matches that search' : 'No creditors yet — create the first one'}
        >
          {!loading && shown.map((c) => (
            <Row key={c.id}>
              <Cell>
                <div className="flex items-start gap-3 min-w-0">
                  <span className="grid place-items-center w-9 h-9 rounded-xl flex-shrink-0
                                   bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300">
                    <Landmark size={16} />
                  </span>
                  <div className="min-w-0">
                    <p className="font-bold text-slate-900 dark:text-white">{c.name}</p>
                    {c.address && (
                      <p className="text-[11.5px] text-slate-400 mt-0.5 line-clamp-1 max-w-md">{c.address}</p>
                    )}
                  </div>
                </div>
              </Cell>
              <Cell className="font-mono text-[12px] whitespace-nowrap">{c.gstNo || '—'}</Cell>
              <Cell className="font-mono text-[12px] whitespace-nowrap">{c.pan || '—'}</Cell>
              <Cell className="font-mono text-[12px] whitespace-nowrap">{c.tan || '—'}</Cell>
              <Cell className="font-mono text-[12px] whitespace-nowrap">{c.cin || '—'}</Cell>
              {/* Money we have paid them that is not yet against a bill. */}
              <Cell className="text-right tabular-nums whitespace-nowrap">
                {c.balance > 0
                  ? <span className="font-semibold text-amber-600 dark:text-amber-400">₹{money(c.balance)}</span>
                  : <span className="text-slate-300 dark:text-slate-600">—</span>}
              </Cell>
              <Cell className="whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <button type="button" title="The purchase bills read from this creditor"
                    onClick={() => setViewing(c)}
                    className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-[12px] font-bold
                               whitespace-nowrap transition
                               text-violet-700 bg-violet-50 hover:bg-violet-100
                               dark:text-violet-300 dark:bg-violet-500/15 dark:hover:bg-violet-500/25">
                    <Receipt size={13} /> View PUR
                  </button>
                  <LedgerButton kind="creditor" id={c.id} name={c.name} />
                  <button type="button" className={btn.icon} title="Edit creditor" onClick={() => openEdit(c)}>
                    <Pencil size={15} />
                  </button>
                  <button type="button" className={btn.iconDanger} title="Remove creditor" onClick={() => setToDelete(c)}>
                    <Trash2 size={15} />
                  </button>
                </div>
              </Cell>
            </Row>
          ))}
        </Table>
      </Panel>

      <PurViewerModal
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        creditor={viewing}
      />

      <CreditorForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        initial={editing}
        onSaved={() => load()}
      />

      <ConfirmModal
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
        busy={deleting}
        title="Remove this creditor?"
        subtitle={toDelete?.name}
        confirmLabel="Remove"
        busyLabel="Removing…"
      >
        <p className="text-[13px] text-slate-600 dark:text-slate-300">
          Purchase bills already read against {toDelete?.name} are kept. Only the creditor record goes.
        </p>
      </ConfirmModal>
    </div>
  );
};

export default CreditorMaster;
